import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { MarketingNav } from "../components/Nav";
import Footer from "../components/Footer";

const values = [
  {
    n: "01",
    title: "Intention over impulse",
    body: "Every feature is designed to slow you down in the best way. A finite shortlist. Structured rounds. No infinite feed.",
  },
  {
    n: "02",
    title: "Quality over quantity",
    body: "We believe you only need one. Bora Moyo is built around that belief — not the metrics of maximum engagement.",
  },
  {
    n: "03",
    title: "Warmth over performance",
    body: "We measure green flags: consistency, effort, kindness. Not red letters, not public scores, not competition.",
  },
  {
    n: "04",
    title: "Privacy as a default",
    body: "Your shortlist is private. Your journey is private. Nothing about your experience is performative.",
  },
];

const team = [
  {
    name: "Amara Diallo",
    role: "Co-founder & CEO",
    note: "Former product lead at a global matchmaking firm. Believes the best relationships start with the best decisions.",
  },
  {
    name: "James Osei",
    role: "Co-founder & CTO",
    note: "Built dating infrastructure at scale. Now building something smaller, better, and more deliberate.",
  },
  {
    name: "Lena Voss",
    role: "Head of Trust & Safety",
    note: "Eight years in online safety. Joined because she believes a safer app is a better app.",
  },
];

export default function About() {
  useEffect(() => {
    document.title = "About — Bora Moyo";
  }, []);

  return (
    <div className="bg-[#05070d] min-h-screen text-white">
      <MarketingNav />

      {/* Hero */}
      <section className="pt-40 pb-20 md:pt-52 md:pb-28 px-6 md:px-12">
        <div className="mx-auto max-w-4xl">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-5">Our story</div>
          <h1 className="font-display text-5xl md:text-7xl leading-[1.02] tracking-tight mb-8">
            Built for people who'd<br />
            <span className="italic text-[#D4AF37]">rather decide</span><br />
            than scroll.
          </h1>
          <p className="text-white/60 text-xl max-w-2xl leading-relaxed">
            Bora Moyo began with a simple frustration: the best people we know struggle most with modern dating — not because they lack options, but because options without structure lead nowhere.
          </p>
          <p className="mt-6 text-white/50 text-lg max-w-2xl leading-relaxed">
            Most swipe apps feel generic because they rely on fast, shallow interactions. BoraMoyo was created to introduce a more intentional system — one designed to produce better alignment and more meaningful connections.
          </p>
        </div>
      </section>

      {/* Divider */}
      <div className="mx-auto max-w-6xl px-6 md:px-12">
        <div className="h-px bg-white/5" />
      </div>

      {/* What the name means */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="mx-auto max-w-6xl grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4">The name</div>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-6">
              What does<br /><em>Bora Moyo</em> mean?
            </h2>
            <p className="text-white/60 leading-relaxed mb-5">
              <em className="text-[#F2DE94]">Bora Moyo</em> is Swahili for <em className="text-[#F2DE94]">"the best heart."</em> It is what we are all looking for — not the most impressive résumé, not the most attractive profile, but the person whose heart aligns with yours.
            </p>
            <p className="text-white/60 leading-relaxed">
              The name anchors everything we build. Every feature, every round, every design decision points toward one thing: helping you find the one with the best heart for you.
            </p>
          </div>
            <div className="relative rounded-3xl bg-[#0C111D] p-8 flex items-center justify-center">
            <img
                src="/logo.png"
              alt="Bora Moyo — the best heart"
                className="max-h-[320px] w-auto object-contain opacity-95"
            />
              <div className="absolute inset-0 pointer-events-none"></div>
            <div className="absolute bottom-8 left-8 right-8">
              <div className="font-display text-3xl italic text-[#D4AF37]">"The best heart."</div>
              <div className="text-xs tracking-[0.22em] uppercase text-white/50 mt-2">Swahili &middot; Bora Moyo</div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 border-t border-white/5 px-6 md:px-12">
        <div className="mx-auto max-w-6xl">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4">What we believe</div>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-14">
            Our core values
          </h2>
          <div className="grid md:grid-cols-2 gap-5">
            {values.map((v) => (
              <div key={v.n} className="backdrop-blur-xl bg-[#0C111D]/65 border border-white/8 rounded-2xl p-7">
                <div className="font-mono text-xs text-[#D4AF37] tracking-[0.2em] mb-4">{v.n}</div>
                <div className="font-display text-2xl mb-3">{v.title}</div>
                <p className="text-white/55 text-sm leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 border-t border-white/5 px-6 md:px-12">
        <div className="mx-auto max-w-6xl">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4">The people</div>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-14">
            Who built this
          </h2>
          <div className="grid md:grid-cols-3 gap-5">
            {team.map((p) => (
              <div key={p.name} className="backdrop-blur-xl bg-[#0C111D]/65 border border-white/8 rounded-2xl p-7">
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 border border-[#D4AF37]/20 flex items-center justify-center mb-5">
                  <span className="font-display text-2xl text-[#D4AF37]">{p.name[0]}</span>
                </div>
                <div className="font-display text-xl mb-1">{p.name}</div>
                <div className="text-[11px] tracking-[0.18em] uppercase text-[#D4AF37]/80 mb-4">{p.role}</div>
                <p className="text-white/55 text-sm leading-relaxed">{p.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-white/5 px-6 md:px-12 text-center">
        <div className="mx-auto max-w-2xl">
          <div className="font-display text-4xl md:text-5xl leading-tight mb-6">
            Ready to decide<br />
            <span className="italic text-[#D4AF37]">with intention?</span>
          </div>
          <Link
            to="/onboarding"
            className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#080B14] font-semibold px-8 py-4 hover:scale-[1.02] transition-transform"
          >
            Begin your journey
            <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current"><path d="M5 12h14m-5-5 5 5-5 5"/></svg>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
